import {computed, MaybeRefOrGetter, toValue} from "vue";
import {MSSantePrefill} from "@/mssante/interfaces/SecureMessaging";
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import formatHelper from "@/core/helpers/formatHelper";

/**
 * The MSSanté messages informing a patient's doctor, agreed with the patient's gender.
 * The pharmacy name and contact are appended by the HDS footer.
 *
 * @param consumer The patient the message is about.
 */
const usePatientMSSanteMessages = (consumer: MaybeRefOrGetter<Customer | Beneficiary | undefined>) => {
    /**
     * Pick the masculine or feminine form matching the patient's gender, both when unknown.
     */
    const agree = (masculine: string, feminine: string, both: string): string => {
        const civility = formatHelper.civility(toValue(consumer)?.information?.gender);

        return civility === 'Monsieur' ? masculine : civility === 'Madame' ? feminine : both;
    };

    /**
     * The patient name preceded by their civility, empty while the patient is unknown.
     */
    const patientName = computed((): string => {
        const information = toValue(consumer)?.information;
        const name = [information?.first_name, information?.last_name?.toUpperCase()].filter(Boolean).join(' ');

        return name ? `${agree('M.', 'Mme', 'M./Mme')} ${name}` : '';
    });

    /**
     * The opening sentence presenting the patient, completed by what the message is about.
     */
    const introduction = (about: string): string => {
        const birthDate = toValue(consumer)?.information?.formatted_birth_date ?? '';

        return `Je vous informe que votre ${agree('patient', 'patiente', 'patient(e)')}, ${patientName.value}, `
            + `${agree('né', 'née', 'né(e)')} le ${birthDate}, ${about}`;
    };

    const closing = (signatory: string): string[] => [
        'Je reste naturellement à votre disposition pour toute information complémentaire.',
        'Bien confraternellement,',
        signatory,
    ];

    /**
     * The message informing the doctor of a vaccination, the dose being left for the pharmacist
     * to complete.
     *
     * @param vaccine The administered vaccine.
     * @param details Extra lines describing the vaccination (e.g. the batch number).
     * @param signatory The pharmacist signing the message.
     * @param administeredToday Whether the vaccination is known to be done today, otherwise its
     *   date is left for the pharmacist to complete.
     */
    const vaccinationMessage = (
        vaccine: string,
        details: string[],
        signatory: string,
        administeredToday = true,
    ): MSSantePrefill => ({
        subject: `Vaccination de ${patientName.value}`,
        body: [
            'Cher Confrère / Chère Consœur,',
            introduction(`a été ${agree('vacciné', 'vaccinée', 'vacciné(e)')}${administeredToday ? ' ce jour' : ''} `
                + 'au sein de notre officine.'),
            `Vaccin administré : ${vaccine}`,
            ...details,
            `Date d’administration : ${administeredToday ? new Date().toLocaleDateString('fr-FR') : '[date]'}`,
            'Dose : [numéro ou dosage]',
            'La vaccination s’est déroulée sans difficulté particulière.',
            ...closing(signatory),
        ].join('\n'),
    });

    /**
     * The message informing the doctor that the patient is followed in a program, the
     * purpose being left for the pharmacist to complete.
     *
     * @param programName The program the patient is followed in.
     * @param signatory The pharmacist signing the message.
     */
    const programMessage = (programName: string, signatory: string): MSSantePrefill => ({
        subject: `Suivi de ${patientName.value} — ${programName}`,
        body: [
            'Cher Confrère / Chère Consœur,',
            introduction(`est ${agree('accompagné', 'accompagnée', 'accompagné(e)')} au sein de notre officine `
                + `dans le cadre du programme « ${programName} ».`),
            '[Précisez ici l’objet de votre message]',
            ...closing(signatory),
        ].join('\n'),
    });

    return {patientName, vaccinationMessage, programMessage};
};

export default usePatientMSSanteMessages;
